import {
  arrayUnion,
  doc,
  increment,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import db from '../firebase/firebaseConfig';

const SHORTLINK_METRICS_COLLECTION = 'shortlinkMetrics';
const SHORTLINK_VISITORS_COLLECTION = 'shortlinkVisitors';
const VISITOR_GEO_CACHE_KEY = 'hcva_shortlink_geo';
const VISITOR_SESSION_ID_KEY = 'hcva_visitor_sid';
const VISITOR_GEO_CACHE_TTL_MS = 60 * 1000;

function getVisitorSessionId() {
  try {
    let id = sessionStorage.getItem(VISITOR_SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem(VISITOR_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function trackedVisitorSessionKey(siteId, visitorSessionId) {
  return `hcva_shortlink_fs_first_${siteId}_${visitorSessionId}`;
}

export function hasTrackedShortlinkVisitorSession(siteId) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return false;
  try {
    const visitorSessionId = getVisitorSessionId();
    return sessionStorage.getItem(
      trackedVisitorSessionKey(normalizedSiteId, visitorSessionId)
    ) === '1';
  } catch {
    return false;
  }
}

/** sessionStorage: set before navigating to shortlink — consumed once on embed load. */
export const SHORTLINK_VISIT_NOTE_KEY = 'hcva_shortlink_visit_note';
export const SHORTLINK_VISIT_FROM_POPUP = 'popup';
export const SHORTLINK_VISIT_FROM_REDIRECT_GATE = 'redirect_gate';

/** Human-readable note for why geo/device row was recorded (embed vs fullscreen). */
export function consumePendingVisitNoteForEmbed() {
  try {
    const v = sessionStorage.getItem(SHORTLINK_VISIT_NOTE_KEY);
    sessionStorage.removeItem(SHORTLINK_VISIT_NOTE_KEY);
    if (v === SHORTLINK_VISIT_FROM_POPUP) {
      return 'Popup screen visit opened the shortlink offer.';
    }
    if (v === SHORTLINK_VISIT_FROM_REDIRECT_GATE) {
      return 'Redirect gate click opened the shortlink offer screen.';
    }
  } catch {
    /* ignore */
  }
  return 'Shortlink offer screen loaded.';
}

export const VISIT_NOTE_FULLSCREEN = 'Entered fullscreen.';

export const VISIT_NOTE_SECURITY_CHECK =
  'Security check screen shown (redirect screen 1 — IP).';

export const VISIT_NOTE_OFFER_SCREEN =
  'Offer page shown (redirect screen 2).';

export const VISIT_NOTE_REDIRECT_RECLICK =
  'Mouse click while on shortlink (redirect gate).';

/** Log-only lines (no visitCount increment) — fullscreen exit, tab close, etc. */
export const VISIT_NOTE_FULLSCREEN_EXIT = 'Exited fullscreen.';
export const VISIT_NOTE_TAB_OR_PAGE_CLOSED =
  'Page unload / left signal detected (tab close, refresh, navigation, or browser left page).';

/** Readable labels for how fullscreen was exited (mirrors clickTracking close ways). */
const FS_EXIT_METHOD_LABELS = {
  esc_button: 'Esc key',
  f11_key: 'F11 key',
  back_button: 'Back button',
  tab_close: 'Tab close / reload',
  'browser/system': 'Browser / system',
};

/** "Exited fullscreen — Esc key." */
export function fullscreenExitNote(method) {
  const label = FS_EXIT_METHOD_LABELS[method] || 'Browser / system';
  return `Exited fullscreen — ${label}.`;
}

/** "Key press: Escape." (display-only key name). */
export function keyPressNote(key, { ctrl, alt, shift, meta } = {}) {
  const named = key === ' ' ? 'Space' : key || 'Unknown';
  const mods = [ctrl && 'Ctrl', alt && 'Alt', shift && 'Shift', meta && 'Meta'].filter(Boolean);
  return `Status: key pressed — Key: ${mods.length ? `${mods.join('+')}+` : ''}${named}.`;
}

/** "Fullscreen close attempt — key pressed: F11." */
export function fullscreenKeyAttemptNote(key, { ctrl, alt, shift, meta } = {}) {
  const named = key === ' ' ? 'Space' : key || 'Unknown';
  const mods = [ctrl && 'Ctrl', alt && 'Alt', shift && 'Shift', meta && 'Meta'].filter(Boolean);
  return `Fullscreen close attempt — status: key pressed — Key: ${mods.length ? `${mods.join('+')}+` : ''}${named}.`;
}

/** "Button click: \"Continue\" at (120, 340)." or "Mouse click at (x, y)." */
export function clickNote({ label, x, y } = {}) {
  const where = x != null && y != null ? ` at (${x}, ${y})` : '';
  if (label) return `Button click: "${String(label).slice(0, 40)}"${where}.`;
  return `Mouse click${where}.`;
}

function getDeviceType() {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|iemobile|blackberry|opera mini/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/** Human-readable OS for admin (macOS / Windows / Linux / iOS / Android / Other). */
export function parseOsLabel(ua, platform) {
  const u = (ua || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(u)) return 'iOS';
  if (/android/.test(u)) return 'Android';
  if (/macintosh|mac os x/.test(u)) return 'macOS';
  if (/windows/.test(u)) return 'Windows';
  if (/linux/.test(u)) return 'Linux';
  const p = (platform || '').toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('win')) return 'Windows';
  return 'Other';
}

/** Browser name from user-agent string. */
export function parseBrowserName(ua) {
  const u = ua || '';
  if (/Edg\//i.test(u)) return 'Edge';
  if (/OPR\/|Opera\/|Opera Mini/i.test(u)) return 'Opera';
  if (/Firefox\//i.test(u)) return 'Firefox';
  if (/Chrome\/|CriOS/i.test(u)) return 'Chrome';
  if (/Safari/i.test(u) && !/Chrome/i.test(u)) return 'Safari';
  return 'Other';
}

/**
 * IP + country (HTTPS, browser-safe). Cached per session.
 * Primary: ipapi.co; fallback: ipify + optional ipwho.is lookup.
 */
async function resolveVisitorGeo() {
  try {
    const raw = sessionStorage.getItem(VISITOR_GEO_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.ip === 'string' &&
        (!parsed.cachedAt || Date.now() - parsed.cachedAt <= VISITOR_GEO_CACHE_TTL_MS)
      ) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }

  const empty = { ip: '', countryName: '', countryCode: '' };

  try {
    const res = await fetch(`https://ipapi.co/json/?ts=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) {
      const d = await res.json();
      if (!d || d.error) throw new Error(d?.reason || 'ipapi error');
      const ip = typeof d.ip === 'string' ? d.ip : '';
      const countryName =
        typeof d.country_name === 'string'
          ? d.country_name
          : typeof d.country === 'string' && d.country.length > 2
            ? d.country
            : '';
      let countryCode =
        typeof d.country_code === 'string'
          ? d.country_code
          : typeof d.country === 'string' && d.country.length === 2
            ? d.country
            : '';
      countryCode = (countryCode || '').toUpperCase().slice(0, 2);
      const out = { ip, countryName: countryName || '', countryCode };
      if (out.ip) {
        try {
          sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify({ ...out, cachedAt: Date.now() }));
        } catch {
          /* ignore */
        }
        return out;
      }
    }
  } catch {
    /* try fallbacks */
  }

  try {
    const res = await fetch(`https://api64.ipify.org?format=json&ts=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return empty;
    const data = await res.json();
    const ip = typeof data?.ip === 'string' ? data.ip : '';
    if (!ip) return empty;

    let countryName = '';
    let countryCode = '';
    try {
      const g = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, { cache: 'no-store' });
      if (g.ok) {
        const w = await g.json();
        if (w.success !== false && w.country) {
          countryName = typeof w.country === 'string' ? w.country : '';
          countryCode =
            typeof w.country_code === 'string'
              ? w.country_code.toUpperCase().slice(0, 2)
              : '';
        }
      }
    } catch {
      /* country unknown */
    }

    const out = { ip, countryName, countryCode };
    try {
      sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify({ ...out, cachedAt: Date.now() }));
    } catch {
      /* ignore */
    }
    return out;
  } catch {
    return empty;
  }
}

export async function trackShortlinkFullscreenTrigger({
  siteId,
  slug,
  sourceDomain,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  try {
    await setDoc(
      doc(db, SHORTLINK_METRICS_COLLECTION, normalizedSiteId),
      {
        siteId: normalizedSiteId,
        fullscreenTriggers: increment(1),
        lastSlug: slug || '',
        lastSourceDomain: sourceDomain || '',
        lastFullscreenAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('[shortlinkMetrics] fullscreen trigger write failed:', err);
  }
}

export async function trackShortlinkVisitor({
  siteId,
  slug,
  sourceDomain,
  visitNote = '',
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  const geo = await resolveVisitorGeo();
  const ua = navigator.userAgent || '';
  const language = navigator.language || '';
  const platform = navigator.platform || '';
  const osLabel = parseOsLabel(ua, platform);
  const browserName = parseBrowserName(ua);
  const visitorSessionId = getVisitorSessionId();
  const sessionRef = doc(
    db,
    SHORTLINK_VISITORS_COLLECTION,
    normalizedSiteId,
    'sessions',
    visitorSessionId
  );

  const iso = new Date().toISOString();
  const logLine = `${iso}|${visitNote || '—'}`;

  const firstDocKey = trackedVisitorSessionKey(normalizedSiteId, visitorSessionId);
  let isFirstWriteToSessionDoc = false;
  try {
    isFirstWriteToSessionDoc = !sessionStorage.getItem(firstDocKey);
  } catch {
    isFirstWriteToSessionDoc = true;
  }

  const payload = {
    visitorSessionId,
    siteId: normalizedSiteId,
    slug: slug || '',
    lastSlug: slug || '',
    sourceDomain: sourceDomain || '',
    ipAddress: geo.ip || '',
    countryName: geo.countryName || '',
    countryCode: geo.countryCode || '',
    deviceType: getDeviceType(),
    osLabel,
    browserName,
    userAgent: ua,
    language,
    platform,
    lastVisitNote: visitNote || '',
    lastVisitedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    visitLog: arrayUnion(logLine),
    visitCount: increment(1),
  };

  if (isFirstWriteToSessionDoc) {
    payload.firstVisitedAt = serverTimestamp();
  }

  try {
    await setDoc(sessionRef, payload, { merge: true });
    try {
      sessionStorage.setItem(firstDocKey, '1');
    } catch {
      /* ignore */
    }
  } catch (err) {
    console.error('[shortlinkMetrics] visitor session write failed:', err);
  }
}

/**
 * Append one line to visitLog without incrementing visitCount (for exit / unload events).
 */
export function appendShortlinkVisitLogNote({
  siteId,
  slug,
  sourceDomain,
  visitNote = '',
  requireExistingSession = false,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId || !visitNote) return;

  const visitorSessionId = getVisitorSessionId();
  if (requireExistingSession && !hasTrackedShortlinkVisitorSession(normalizedSiteId)) {
    return;
  }
  const sessionRef = doc(
    db,
    SHORTLINK_VISITORS_COLLECTION,
    normalizedSiteId,
    'sessions',
    visitorSessionId
  );
  const iso = new Date().toISOString();
  const logLine = `${iso}|${visitNote}`;

  void setDoc(
    sessionRef,
    {
      siteId: normalizedSiteId,
      slug: slug || '',
      lastSlug: slug || '',
      sourceDomain: sourceDomain || '',
      lastVisitNote: visitNote,
      lastVisitedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      visitLog: arrayUnion(logLine),
    },
    { merge: true }
  ).catch((err) => {
    console.error('[shortlinkMetrics] append visit log failed:', err);
  });
}

/** Update live session status fields without adding noisy rows to visitLog. */
export function updateShortlinkVisitorSessionStatus({
  siteId,
  slug,
  sourceDomain,
  sessionState,
  fullscreenState,
  fullscreenMode,
  fullscreenNative,
  fullscreenPseudo,
  fullscreenRequestStatus,
  fullscreenRequestError,
  fullscreenRequestAt,
  keyboardLockStatus,
  pageFocused,
  pageVisibility,
  viewportMatchesScreen,
  fullscreenExitMethod,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  const visitorSessionId = getVisitorSessionId();
  if (!hasTrackedShortlinkVisitorSession(normalizedSiteId)) return;

  const payload = {
    siteId: normalizedSiteId,
    updatedAt: serverTimestamp(),
  };
  if (slug != null) {
    payload.slug = slug || '';
    payload.lastSlug = slug || '';
  }
  if (sourceDomain != null) payload.sourceDomain = sourceDomain || '';
  if (sessionState) {
    payload.sessionState = sessionState;
    payload.lastHeartbeatAt = serverTimestamp();
  }
  if (fullscreenState) {
    payload.fullscreenState = fullscreenState;
    payload.fullscreenUpdatedAt = serverTimestamp();
  }
  if (fullscreenMode != null) payload.fullscreenMode = fullscreenMode || '';
  if (fullscreenNative != null) payload.fullscreenNative = Boolean(fullscreenNative);
  if (fullscreenPseudo != null) payload.fullscreenPseudo = Boolean(fullscreenPseudo);
  if (fullscreenRequestStatus != null) {
    payload.fullscreenRequestStatus = fullscreenRequestStatus || '';
  }
  if (fullscreenRequestError != null) {
    payload.fullscreenRequestError = fullscreenRequestError || '';
  }
  if (fullscreenRequestAt) {
    payload.fullscreenRequestAt = new Date(fullscreenRequestAt);
  }
  if (keyboardLockStatus != null) payload.keyboardLockStatus = keyboardLockStatus || '';
  if (pageFocused != null) payload.pageFocused = Boolean(pageFocused);
  if (pageVisibility != null) payload.pageVisibility = pageVisibility || '';
  if (viewportMatchesScreen != null) {
    payload.viewportMatchesScreen = Boolean(viewportMatchesScreen);
  }
  if (fullscreenExitMethod) payload.fullscreenExitMethod = fullscreenExitMethod;

  void setDoc(
    doc(
      db,
      SHORTLINK_VISITORS_COLLECTION,
      normalizedSiteId,
      'sessions',
      visitorSessionId
    ),
    payload,
    { merge: true }
  ).catch((err) => {
    console.error('[shortlinkMetrics] session status update failed:', err);
  });
}
